import React from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";

type ChecklistStatus = "" | "completed" | "skipped" | "blocked";

interface InformationRequest {
  id?: string;
  question?: string;
}

interface TechnicianChecklistReviewProps {
  requests: InformationRequest[];
  answers: Record<string, string>;
  disabled?: boolean;
  onAnswersChange: (answers: Record<string, string>) => void;
}

interface LocalResult {
  status: ChecklistStatus;
  note: string;
}

function parseAnswer(value: string): LocalResult {
  try {
    const parsed = JSON.parse(value || "{}");
    return {
      status: ["completed", "skipped", "blocked"].includes(parsed.status) ? parsed.status : "",
      note: String(parsed.note || ""),
    };
  } catch {
    return { status: "", note: "" };
  }
}

function validEncodedAnswer(result: LocalResult): string {
  if (!result.status) return "";
  if ((result.status === "skipped" || result.status === "blocked") && !result.note.trim()) {
    return "";
  }
  return JSON.stringify({ status: result.status, note: result.note.trim() });
}

const TechnicianChecklistReview: React.FC<TechnicianChecklistReviewProps> = ({
  requests,
  answers,
  disabled = false,
  onAnswersChange,
}) => {
  const [results, setResults] = React.useState<Record<string, LocalResult>>({});
  const requestsKey = JSON.stringify(requests);

  React.useEffect(() => {
    const next: Record<string, LocalResult> = {};
    requests.forEach((request) => {
      const id = String(request.id || "");
      next[id] = parseAnswer(answers[id] || "");
    });
    setResults(next);
    // Reinitialize for a new checklist, but do not reset an in-progress skipped/blocked
    // selection while its required note is still empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestsKey]);

  const updateResult = (id: string, patch: Partial<LocalResult>) => {
    const nextResult = { ...(results[id] || { status: "", note: "" }), ...patch };
    setResults((previous) => ({ ...previous, [id]: nextResult }));
    onAnswersChange({ ...answers, [id]: validEncodedAnswer(nextResult) });
  };

  return (
    <Box>
      <Typography sx={{ color: "#666", fontSize: 13, mb: 2 }}>
        Resolve every required task. A note is required when a task is skipped or blocked.
      </Typography>
      {requests.map((request, index) => {
        const id = String(request.id || "");
        const result = results[id] || { status: "", note: "" };
        const [title, ...instructionLines] = String(request.question || "").split("\n");
        return (
          <Box key={id} sx={{ border: "1px solid #E5E7EB", borderRadius: 1.5, p: 1.5, mb: 1.5 }}>
            <Typography sx={{ fontWeight: 600, color: "#333", mb: 0.5 }}>
              {index + 1}. {title}
            </Typography>
            {instructionLines.length > 0 && (
              <Typography sx={{ color: "#666", fontSize: 13, mb: 1.5 }}>
                {instructionLines.join("\n")}
              </Typography>
            )}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" }, gap: 1.5 }}>
              <TextField
                select
                size="small"
                label="Status"
                value={result.status}
                onChange={(event) => updateResult(id, { status: event.target.value as ChecklistStatus })}
                disabled={disabled}
                required
              >
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="skipped">Skipped</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
              </TextField>
              <TextField
                size="small"
                label={
                  result.status === "skipped" || result.status === "blocked"
                    ? "Note (required)"
                    : "Note (optional)"
                }
                value={result.note}
                onChange={(event) => updateResult(id, { note: event.target.value })}
                disabled={disabled}
                multiline
                minRows={1}
                error={
                  (result.status === "skipped" || result.status === "blocked") &&
                  !result.note.trim()
                }
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default TechnicianChecklistReview;
